/** @format */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  type SOPVersion,
  type StepEditPayload,
  type Step,
  SOP,
} from "../types/sop";
import { useSopStore, type SopListFilters } from "./useSopStore";
import { useSopSelectionStore } from "./useSopSelectionStore";
import { useSopEditStore } from "./useSopEditStore";
import { api } from "../api/client";
import {
  isVersionConflict,
  isVersionLockedError,
  getVersionLockedMessage,
  VERSION_CONFLICT_MESSAGE,
} from "../api/sopErrors";

export type SaveSOPResult =
  | { success: true }
  | { success: false; error: string; versionConflict?: boolean };

interface TrackChangesActions {
  fetchSOPs: (options?: {
    page?: number;
    limit?: number;
  } & SopListFilters) => Promise<void>;
  fetchSOPById: (sopId: string, versionId?: string) => Promise<void>;
  fetchPendingVerificationSOPs: (options?: {
    page?: number;
    limit?: number;
  } & SopListFilters) => Promise<void>;
  addOrUpdateSop: (sop: SOP) => void;
  updateSopTitleById: (sopId: string, newTitle: string) => Promise<void>;
  setSelectedSOP: (sopId: string) => void;
  editStep: (payload: StepEditPayload) => Promise<void>;
  addStep: (stepIndex: number) => Promise<void>;
  deleteStep: (stepIndex: number) => Promise<void>;
  reorderSteps: (fromIndex: number, toIndex: number) => Promise<void>;
  saveSOPChanges: (updatedSteps: Step[]) => Promise<SaveSOPResult>;
  setSelectedVersion: (version: SOPVersion) => void;
  setLatestVersion: () => void;
  setBaseVersion: (version: SOPVersion | null) => void;
  setCompareVersion: (version: SOPVersion | null) => void;
  addVersionToSOP: (sopId: string, version: SOPVersion, newSop?: true) => void;
  clearError: () => void;
  refreshSelectedSOP: () => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;

  // Selectors for computed state
  getSelectedSOP: () => SOP | null;
  getSopById: (sopId: string) => SOP | null;
  getLatestVersion: (sopId: string) => SOPVersion | null;
  getVersionList: (sopId: string) => import("../types/sop").VersionListItem[];
}

interface TrackChangesState extends TrackChangesActions {
  // Re-export state from other stores for convenience
  sops: SOP[];
  selectedSOP: string;
  selectedVersion: SOPVersion | null;
  latestVersion: SOPVersion | null;
  baseVersion: SOPVersion | null;
  compareVersion: SOPVersion | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;
}

export const useTrackChangesStore = create<TrackChangesState>()(
  devtools(
    (set) => ({
      // Initialize with empty state - will be updated by subscriptions
      sops: [],
      selectedSOP: "",
      selectedVersion: null,
      latestVersion: null,
      isLoading: false,
      isUpdating: false,
      error: null,
      hasUnsavedChanges: false,

      // Re-export actions from other stores
      fetchSOPs: (options) =>
        useSopStore.getState().fetchSOPs(options),
      fetchSOPById: (sopId: string, versionId?: string) =>
        useSopStore.getState().fetchSOPById(sopId, versionId),
      fetchPendingVerificationSOPs: (options) =>
        useSopStore.getState().fetchPendingVerificationSOPs(options),
      addOrUpdateSop: (sop: SOP) => useSopStore.getState().addOrUpdateSop(sop),
      updateSopTitleById: (sopId: string, newTitle: string) =>
        useSopStore.getState().updateSopTitleById(sopId, newTitle),
      setSelectedSOP: (sopId: string) =>
        useSopSelectionStore.getState().setSelectedSOP(sopId),
      editStep: (payload: StepEditPayload) =>
        useSopEditStore.getState().editStep(payload),
      setSelectedVersion: (version: SOPVersion) =>
        useSopSelectionStore.getState().setSelectedVersion(version),
      setLatestVersion: () =>
        useSopSelectionStore.getState().setLatestVersion(),
      setBaseVersion: (version: SOPVersion | null) =>
        useSopSelectionStore.getState().setBaseVersion(version),
      setCompareVersion: (version: SOPVersion | null) =>
        useSopSelectionStore.getState().setCompareVersion(version),
      addVersionToSOP: (sopId: string, version: SOPVersion, newSop?: true) =>
        useSopStore.getState().addVersionToSOP(sopId, version, newSop),
      clearError: () => {
        useSopStore.getState().clearError();
        useSopEditStore.getState().clearError();
      },
      refreshSelectedSOP: () =>
        useSopSelectionStore.getState().refreshSelectedSOP(),

      setHasUnsavedChanges: (hasChanges: boolean) =>
        set({ hasUnsavedChanges: hasChanges }),

      // New step management functions
      addStep: async (stepIndex: number) => {
        const { selectedSOP } = useSopSelectionStore.getState();
        const sop = useSopStore.getState().getSopById(selectedSOP);
        if (!sop) return;

        const latestVersion = sop.getLatestVersion();
        if (!latestVersion) return;

        const newStep: Step = {
          title: `Step ${stepIndex + 1}`,
          details: "",
          parts: [],
          tools: [],
          checklist: [],
          imageReferences: [],
        };

        // Optimistic update
        useSopStore.setState((state) => ({
          sops: state.sops.map((s) => {
            if (s.sopId === selectedSOP) {
              const updatedVersions = s.versions.map((v) => {
                if (v.versionId === latestVersion.versionId) {
                  const newSteps = [...v.steps];
                  newSteps.splice(stepIndex, 0, newStep);
                  return { ...v, steps: newSteps };
                }
                return v;
              });
              return new SOP(
                s.sopId,
                s.sopTitle,
                updatedVersions,
                s.createdAt,
                s?.processingStatus,
                s?.processingTimeSeconds,
                s.bom,
                s.revisions,
                s.organizationId,
                s.creatorEmail,
                s.organizationName,
              );
            }
            return s;
          }),
        }));

        // Refresh the selection store to reflect the changes
        useSopSelectionStore.getState().refreshSelectedSOP();

        set({ hasUnsavedChanges: true });
      },

      deleteStep: async (stepIndex: number) => {
        const { selectedSOP } = useSopSelectionStore.getState();
        const sop = useSopStore.getState().getSopById(selectedSOP);
        if (!sop) return;

        const latestVersion = sop.getLatestVersion();
        if (!latestVersion) return;

        // Optimistic update
        useSopStore.setState((state) => ({
          sops: state.sops.map((s) => {
            if (s.sopId === selectedSOP) {
              const updatedVersions = s.versions.map((v) => {
                if (v.versionId === latestVersion.versionId) {
                  const newSteps = v.steps.filter(
                    (_, index) => index !== stepIndex,
                  );
                  return { ...v, steps: newSteps };
                }
                return v;
              });
              return new SOP(
                s.sopId,
                s.sopTitle,
                updatedVersions,
                s.createdAt,
                s?.processingStatus,
                s?.processingTimeSeconds,
                s.bom,
                s.revisions,
                s.organizationId,
                s.creatorEmail,
                s.organizationName,
              );
            }
            return s;
          }),
        }));

        // Refresh the selection store to reflect the changes
        useSopSelectionStore.getState().refreshSelectedSOP();

        set({ hasUnsavedChanges: true });
      },

      reorderSteps: async (fromIndex: number, toIndex: number) => {
        const { selectedSOP } = useSopSelectionStore.getState();
        const sop = useSopStore.getState().getSopById(selectedSOP);
        if (!sop) return;

        const latestVersion = sop.getLatestVersion();
        if (!latestVersion) return;

        // Optimistic update
        useSopStore.setState((state) => ({
          sops: state.sops.map((s) => {
            if (s.sopId === selectedSOP) {
              const updatedVersions = s.versions.map((v) => {
                if (v.versionId === latestVersion.versionId) {
                  const newSteps = [...v.steps];
                  const [movedStep] = newSteps.splice(fromIndex, 1);
                  newSteps.splice(toIndex, 0, movedStep);
                  return { ...v, steps: newSteps };
                }
                return v;
              });
              return new SOP(
                s.sopId,
                s.sopTitle,
                updatedVersions,
                s.createdAt,
                s?.processingStatus,
                s?.processingTimeSeconds,
                s.bom,
                s.revisions,
                s.organizationId,
                s.creatorEmail,
                s.organizationName,
              );
            }
            return s;
          }),
        }));

        // Refresh the selection store to reflect the changes
        useSopSelectionStore.getState().refreshSelectedSOP();

        set({ hasUnsavedChanges: true });
      },

      saveSOPChanges: async (updatedSteps: Step[]): Promise<SaveSOPResult> => {
        const { selectedSOP } = useSopSelectionStore.getState();
        const sop = useSopStore.getState().getSopById(selectedSOP);
        if (!sop) {
          return { success: false, error: "SOP not found" };
        }

        const latestVersion = sop.getLatestVersion();
        if (!latestVersion) {
          return { success: false, error: "No version to update" };
        }

        try {
          set({ isUpdating: true, error: null });

          // Backend sync – PUT /sops/<sop_id>/steps (backend resolves latest version)
          const res = await api.put(`/sops/${sop.sopId}/steps`, {
            steps: updatedSteps,
          });

          const data = res.data;
          // Always update state with version returned by backend so next save uses latest
          const newVersion: SOPVersion = {
            versionId: data.version_id,
            versionNum: data.version_num,
            versionTitle:
              data.version_title && data.version_title?.trim() !== ""
                ? data.version_title
                : `Version ${data.version_num}`,
            steps: updatedSteps,
            titlePage: latestVersion.titlePage,
            editedAt: new Date().toISOString(),
            status: latestVersion.status ?? "draft",
          };

          useSopStore.getState().addVersionToSOP(sop.sopId, newVersion);

          useSopSelectionStore.setState({
            selectedVersion: newVersion,
            latestVersion: newVersion,
          });

          set({ isUpdating: false, hasUnsavedChanges: false });
          return { success: true };
        } catch (error) {
          set({ isUpdating: false });

          if (isVersionLockedError(error)) {
            const msg = getVersionLockedMessage(error);
            set({ error: msg });
            return { success: false, error: msg };
          }

          if (isVersionConflict(error)) {
            // Auto-recover for single-user / stale-client cases:
            // refresh to latest versionId and retry once with the user's current edits.
            try {
              await useSopStore.getState().fetchSOPs();
              useSopSelectionStore.getState().refreshSelectedSOP();
              const refreshed = useSopStore
                .getState()
                .getLatestVersion(sop.sopId);
              if (!refreshed) {
                set({ error: VERSION_CONFLICT_MESSAGE });
                return {
                  success: false,
                  error: VERSION_CONFLICT_MESSAGE,
                  versionConflict: true,
                };
              }

              set({ isUpdating: true, error: null });
              const res2 = await api.put(`/sops/${sop.sopId}/steps`, {
                steps: updatedSteps,
              });
              const data2 = res2.data;
              const newVersion2: SOPVersion = {
                versionId: data2.version_id,
                versionNum: data2.version_num,
                versionTitle:
                  data2.version_title && data2.version_title?.trim() !== ""
                    ? data2.version_title
                    : `Version ${data2.version_num}`,
                steps: updatedSteps,
                titlePage: refreshed.titlePage,
                editedAt: new Date().toISOString(),
                status: refreshed.status ?? "draft",
              };

              useSopStore.getState().addVersionToSOP(sop.sopId, newVersion2);
              useSopSelectionStore.setState({
                selectedVersion: newVersion2,
                latestVersion: newVersion2,
              });
              set({ isUpdating: false, hasUnsavedChanges: false });
              return { success: true };
            } catch {
              set({ isUpdating: false, error: VERSION_CONFLICT_MESSAGE });
              return {
                success: false,
                error: VERSION_CONFLICT_MESSAGE,
                versionConflict: true,
              };
            }
          }

          const message =
            error instanceof Error
              ? error.message
              : "Failed to save SOP changes";
          set({ error: message });
          return { success: false, error: message };
        }
      },

      // Selectors for computed state
      getSelectedSOP: () => useSopSelectionStore.getState().getSelectedSOP(),
      getSopById: (sopId: string) => useSopStore.getState().getSopById(sopId),
      getLatestVersion: (sopId: string) =>
        useSopStore.getState().getLatestVersion(sopId),
      getVersionList: (sopId: string) =>
        useSopStore.getState().getVersionList(sopId),
    }),
    {
      name: "track-changes-store",
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);

// Create a hook that provides the current state from all stores
export const useTrackChangesState = () => {
  const sopStoreState = useSopStore();
  const selectionStoreState = useSopSelectionStore();
  const editStoreState = useSopEditStore();
  const trackChangesStore = useTrackChangesStore();

  return {
    // State from individual stores
    sops: sopStoreState.sops,
    pagination: sopStoreState.pagination,
    isLoading: sopStoreState.isLoading,
    error: sopStoreState.error || editStoreState.error,
    selectedSOP: selectionStoreState.selectedSOP,
    selectedVersion: selectionStoreState.selectedVersion,
    latestVersion: selectionStoreState.latestVersion,
    baseVersion: selectionStoreState.baseVersion,
    compareVersion: selectionStoreState.compareVersion,
    isUpdating: editStoreState.isUpdating,
    hasUnsavedChanges: trackChangesStore.hasUnsavedChanges,

    // Actions from track changes store
    fetchSOPs: trackChangesStore.fetchSOPs,
    fetchSOPById: trackChangesStore.fetchSOPById,
    fetchPendingVerificationSOPs:
      trackChangesStore.fetchPendingVerificationSOPs,
    addOrUpdateSop: trackChangesStore.addOrUpdateSop,
    updateSopTitleById: trackChangesStore.updateSopTitleById,
    setSelectedSOP: trackChangesStore.setSelectedSOP,
    editStep: trackChangesStore.editStep,
    addStep: trackChangesStore.addStep,
    deleteStep: trackChangesStore.deleteStep,
    reorderSteps: trackChangesStore.reorderSteps,
    saveSOPChanges: trackChangesStore.saveSOPChanges,
    setSelectedVersion: trackChangesStore.setSelectedVersion,
    setLatestVersion: trackChangesStore.setLatestVersion,
    setBaseVersion: trackChangesStore.setBaseVersion,
    setCompareVersion: trackChangesStore.setCompareVersion,
    addVersionToSOP: trackChangesStore.addVersionToSOP,
    clearError: trackChangesStore.clearError,
    refreshSelectedSOP: trackChangesStore.refreshSelectedSOP,
    setHasUnsavedChanges: trackChangesStore.setHasUnsavedChanges,
    getSelectedSOP: trackChangesStore.getSelectedSOP,
    getSopById: trackChangesStore.getSopById,
    getLatestVersion: trackChangesStore.getLatestVersion,
    getVersionList: trackChangesStore.getVersionList,
  };
};
